import { gql } from '@apollo/client'
import { AppTriggerEnum } from '@chaine/types'

import { AppType } from '../../../_shared/types/app-types'

/**
 * Create an api key for apps that require it
 * @param scopes array of scopes that the key allows

 */
export const CREATE_APP_API_KEY = gql`
  mutation CreateAppAPIKey($appType: String!, $source: String) {
    createAppAPIKey(input: { appType: $appType, source: $source }) {
      value
    }
  }
`
/**
 * Takes a sonar username and password and enables the Sonar app
 * @param scopes array of scopes that the key allows

 */
export const ENABLE_SONAR_APP = gql`
  mutation EnableSonarApp($appType: String!, $username: String!, $password: String!) {
    enableSonarApp(input: { appType: $appType, username: $username, password: $password }) {
      id
    }
  }
`
export const ENABLE_MY_CARRIER_PORTAL_APP = gql`
  mutation EnableMyCarrierPortalApp(
    $appType: String!
    $username: String!
    $onboardingURL: String!
    $password: String!
  ) {
    enableMyCarrierPortalApp(
      input: { appType: $appType, username: $username, onboardingURL: $onboardingURL, password: $password }
    ) {
      id
    }
  }
`
export const ENABLE_RMIS_APP = gql`
  mutation EnableRMISApp($appType: String!, $clientID: String!, $domainName: String, $password: String!) {
    enableRMISApp(input: { appType: $appType, clientID: $clientID, domainName: $domainName, password: $password }) {
      id
    }
  }
`

/**
 * Takes a TAI TMS api key and saves it to the workspace. Once the key is validated, a chaine api key is created to be used for step 2 of the integration.
 * @param scopes array of scopes that the key allows

 */
export const ENABLE_TAI_TMS_APP = gql`
  mutation EnableTaiTMSApp($appType: String!, $apiKey: String!) {
    enableTaiTMSApp(input: { appType: $appType, apiKey: $apiKey }) {
      value
    }
  }
`

/**
 * The input to save client details
 */
export type SaveClientDetailsInput = {
  appType: AppType
}

/**
 * Mutation to save client details when creating a new app integration.
 * Currently only required for Salesforce/Revenova
 */
export const SAVE_CLIENT_DETAILS = gql`
  mutation SaveClientDetails($appType: String!) {
    saveClientDetails(input: { appType: $appType }) {
      id
    }
  }
`

/**
 * The input to save client details
 */
export type SaveClientIDAndSecretInput = {
  appType: AppType
  clientID: string
  clientSecret: string
}

/*
 * Mutation to save client details when creating a new app integration.
 */
export const SAVE_CLIENT_ID_AND_SECRET = gql`
  mutation SaveClientDetails($appType: String!, $clientID: String!, $clientSecret: String!) {
    saveClientIDAndSecret(input: { appType: $appType, clientID: $clientID, clientSecret: $clientSecret }) {
      id
    }
  }
`

export type SaveEncodedCredentialsInput = {
  appType: AppType
  encodedUsernamePassword: string
}

/*
 * Mutation to save Base64 encoded client credentials in the format of username:password
 */
export const SAVE_PROJECT44_CREDENTIALS = gql`
  mutation SaveProject44AppCredentials($appType: String!, $encodedUsernamePassword: String!) {
    saveProject44AppCredentials(input: { appType: $appType, encodedUsernamePassword: $encodedUsernamePassword }) {
      id
    }
  }
`

/**
 * The input to save an app from the workspace
 */
export type EnableProject44AppInput = {
  appType: AppType
}

/*
 * Mutation to save Base64 encoded client credentials in the format of username:password
 */
export const ENABLE_PROJECT44_APP = gql`
  mutation EnableProject44App($appType: String!) {
    enableProject44App(input: { appType: $appType }) {
      id
    }
  }
`
/**
 * The input to save an app from the workspace
 */
export type EnableGeoTabAppInput = {
  appType: AppType
  databaseName: string
  password: string
  username: string
}

/*
 * Mutation to save the username, database and password of user's GeoTab account
 */
export const ENABLE_GEO_TAB_APP = gql`
  mutation EnableGeoTabApp($appType: String!, $username: String!, $databaseName: String!, $password: String!) {
    enableGeoTabApp(
      input: { appType: $appType, username: $username, databaseName: $databaseName, password: $password }
    ) {
      appEnabled
    }
  }
`

export const FOURKITES_CUSTOMER_APP = gql`
  mutation EnableFourKitesCustomerApp($appType: String!, $apiKey: String!, $customerID: String) {
    enableFourKitesCustomerApp(input: { appType: $appType, apiKey: $apiKey, customerID: $customerID }) {
      appEnabled
    }
  }
`

export const ENABLE_TURVO_APP = gql`
  mutation EnableTurvoApp(
    $appType: String!
    $username: String!
    $password: String!
    $apiKey: String!
    $clientID: String!
    $clientSecret: String!
  ) {
    enableTurvoApp(
      input: {
        appType: $appType
        username: $username
        password: $password
        apiKey: $apiKey
        clientID: $clientID
        clientSecret: $clientSecret
      }
    ) {
      appEnabled
    }
  }
`

export const ENABLE_TAILWIND_APP = gql`
  mutation EnableTailwindApp($appType: String!, $apiKey: String!) {
    enableTailwindApp(input: { appType: $appType, apiKey: $apiKey }) {
      appEnabled
    }
  }
`

/**
 * The input for saving and enabling an app from the workspace.
 */
export type EnableHirschbachAppInput = {
  apiKey: string
  apiURL: string
  appType: AppType
}

/*
 * Mutation to save client api key
 */
export const ENABLE_HIRSCHBACH_APP = gql`
  mutation EnableHirschbachApp($appType: String!, $apiKey: String!, $apiURL: String!) {
    enableHirschbachApp(input: { appType: $appType, apiKey: $apiKey, apiURL: $apiURL }) {
      appEnabled
    }
  }
`

/**
 * The input for saving and enabling an app from the workspace.
 */
export type EnableTQLTraxAppInput = {
  appType: AppType
  clientID: string
  ocpApimSubscriptionKey: string
  password: string
  username: string
}

/*
 * Mutation to save client api key
 */
export const ENABLE_TQL_TRAX_APP = gql`
  mutation EnableTQLTraxApp(
    $appType: String!
    $clientID: String!
    $ocpApimSubscriptionKey: String!
    $password: String!
    $username: String!
  ) {
    enableTQLTraxApp(
      input: {
        appType: $appType
        clientID: $clientID
        ocpApimSubscriptionKey: $ocpApimSubscriptionKey
        password: $password
        username: $username
      }
    ) {
      appEnabled
    }
  }
`

/**
 * Uses type {@link SaveClientIDAndSecretInput}
 */
export const ENABLE_CARGO_CHIEF_APP = gql`
  mutation EnableCargoChiefApp($appType: String!, $clientID: String, $clientSecret: String) {
    enableCargoChiefApp(input: { appType: $appType, clientID: $clientID, clientSecret: $clientSecret }) {
      id
    }
  }
`

/**
 * Uses type {@link SaveClientIDAndSecretInput}
 */
export const ENABLE_GREENSCREENS_APP = gql`
  mutation EnableGreenscreensApp($appType: String!, $clientID: String, $clientSecret: String) {
    enableGreenscreensApp(input: { appType: $appType, clientID: $clientID, clientSecret: $clientSecret }) {
      id
    }
  }
`

/**
 * The input to save a state param
 */
export type SaveStateParamInput = {
  appType: AppType
  id: string
}

/**
 * Mutation to save the state params during the process of integrating a new app. This param is used to verify a callback request.
 */
export const SAVE_STATE_PARAM = gql`
  mutation SaveStateParam($id: ID!, $appType: String!) {
    saveStateParam(input: { id: $id, appType: $appType }) {
      id
    }
  }
`

/**
 * The input to save a disable an app from the workspace
 */
export type DisableAppInput = {
  appType: AppType
}

/**
 * Mutation to save the state params during the process of integrating a new app. This param is used to verify a callback request.
 */
export const DISABLE_APP = gql`
  mutation DisableApp($appType: String!) {
    disableApp(input: { appType: $appType }) {
      id
    }
  }
`

/**
 * The input to save an app from the workspace
 */
export type EnableBigRoadAppInput = {
  apiKey: string
  appType: AppType
}

/*
 * Mutation to save client api key
 */
export const ENABLE_BIG_ROAD_APP = gql`
  mutation EnableBigRoadApp($appType: String!, $apiKey: String!) {
    enableBigRoadApp(input: { appType: $appType, apiKey: $apiKey }) {
      appEnabled
    }
  }
`

/*
 * Mutation to enabled email rate confirmation
 */
export const ENABLE_EMAIL_RATE_CONFIRMATION = gql`
  mutation EnableEmailRateConfirmationApp($appType: String!, $allowedEmailAddresses: [String!]) {
    enableEmailRateConfirmationApp(input: { appType: $appType, allowedEmailAddresses: $allowedEmailAddresses }) {
      rateConfirmationEmail
    }
  }
`

/*
 * Mutation to update email rate confirmation
 */
export const UPDATE_EMAIL_RATE_CONFIRMATION = gql`
  mutation UpdateEmailRateConfirmationApp($appType: String!, $allowedEmailAddresses: [String!]) {
    updateEmailRateConfirmationApp(input: { appType: $appType, allowedEmailAddresses: $allowedEmailAddresses }) {
      rateConfirmationEmail
    }
  }
`

/**
 * The input for saving and enabling an app from the workspace.
 */
export type EnableFleetLocateAppInput = {
  apiKey: string
  appType: AppType
  password: string
  username: string
}

/*
 * Mutation to save client api key
 */
export const ENABLE_FLEET_LOCATE_APP = gql`
  mutation EnableFleetLocateApp($appType: String!, $apiKey: String!, $username: String!, $password: String!) {
    enableFleetLocateApp(input: { appType: $appType, apiKey: $apiKey, username: $username, password: $password }) {
      appEnabled
    }
  }
`

/**
 * The input for saving and enabling an app from the workspace.
 */
export type EnableCoretexAppInput = {
  apiKey: string
  appType: AppType
  password: string
  username: string
}

/*
 * Mutation to save client api key
 */
export const ENABLE_CORETEX_APP = gql`
  mutation EnableCoretexApp($appType: String!, $apiKey: String!, $username: String!, $password: String!) {
    enableCoretexApp(input: { appType: $appType, apiKey: $apiKey, username: $username, password: $password }) {
      appEnabled
    }
  }
`

/**
 * The input for saving and enabling an app from the workspace.
 */
export type EnableERoadAppInput = {
  apiKey: string
  appType: AppType
}

/*
 * Mutation to save client api key
 */
export const ENABLE_EROAD_APP = gql`
  mutation EnableERoadApp($appType: String!, $apiKey: String!) {
    enableERoadApp(input: { appType: $appType, apiKey: $apiKey }) {
      appEnabled
    }
  }
`

/**
 * The input for saving and enabling an app from the workspace.
 */
export type EnableHutchAppInput = {
  apiKey: string
  appType: AppType
}

/*
 * Mutation to save client api key
 */
export const ENABLE_HUTCH_APP = gql`
  mutation EnableHutchApp($appType: String!, $apiKey: String!) {
    enableHutchApp(input: { appType: $appType, apiKey: $apiKey }) {
      appEnabled
    }
  }
`

/**
 * The input to enable or update the FourKitesCarrier app
 */
export type FourKitesCarrierEnableAndUpdateAppInput = {
  apiKey: string
  appType: AppType
  /**
   * The customers for which the data should flow to Four Kites for. This will contain an array of customer objects.
   */
  customers: TCustomer[]
}

/*
 * Enables the FourKitesCarrier app and sets the customers for which the data should flow to Four Kites for.
 */
export const ENABLE_FOUR_KITES_CARRIER_APP = gql`
  mutation EnableFourKitesCarrierApp(
    $appType: String!
    $apiKey: String!
    $customers: [FreightVisibilityAppCustomerDTO!]
  ) {
    enableFourKitesCarrierApp(input: { appType: $appType, apiKey: $apiKey, customers: $customers }) {
      id
    }
  }
`

/**
 * The input for saving and enabling the tive app from the workspace.
 */
export type EnableTiveAppInput = {
  appType: AppType
  clientID: string
  clientSecret: string
}

/*
 * Mutation to save client ID and secret
 */
export const ENABLE_TIVE_APP = gql`
  mutation EnableTiveApp($appType: String!, $clientID: String!, $clientSecret: String!) {
    enableTiveApp(input: { appType: $appType, clientID: $clientID, clientSecret: $clientSecret }) {
      appEnabled
    }
  }
`

/**
 * The input for saving and enabling the Convoy app from the workspace.
 */
export type EnableConvoyAppInput = {
  appType: AppType
  enableAfterCreation: boolean
  source: string
}

/*
 * Mutation to save EncodedAPIKeyWorkspaceIDAndSource
 */
export const ENABLE_CONVOY_APP = gql`
  mutation CreateAppAPIKey($appType: String!, $source: String!, $enableAfterCreation: Boolean!) {
    createAppAPIKey(input: { appType: $appType, source: $source, enableAfterCreation: $enableAfterCreation }) {
      value
    }
  }
`

/**
 * The input for saving and enabling the Orbcomm app from the workspace.
 */
export type EnableOrbcommAppInput = {
  appType: AppType
  enableAfterCreation: boolean
  isCronBasedOrEventBased?: AppTriggerEnum
  source: string
}

/*
 * Mutation to save EncodedAPIKeyWorkspaceIDAndSource
 */
export const ENABLE_ORBCOMM_APP = gql`
  mutation CreateAppAPIKey(
    $appType: String!
    $source: String!
    $enableAfterCreation: Boolean!
    $isCronBasedOrEventBased: String!
  ) {
    createAppAPIKey(
      input: {
        appType: $appType
        source: $source
        enableAfterCreation: $enableAfterCreation
        isCronBasedOrEventBased: $isCronBasedOrEventBased
      }
    ) {
      value
    }
  }
`

/**
 * The input for saving and enabling an app from the workspace.
 */
export type EnableVerizonTelogisAppInput = {
  appType: AppType
  password: string
  username: string
}

/**
 * The input for saving and enabling an app from the workspace.
 */
export type EnableVerizonRevealAppInput = {
  appType: AppType
  password: string
  username: string
}

/*
 * Mutation to save client api key
 */
export const ENABLE_VERIZON_TELOGIS_APP = gql`
  mutation EnableVerizonTelogisApp($appType: String!, $username: String!, $password: String!) {
    enableVerizonTelogisApp(input: { appType: $appType, username: $username, password: $password }) {
      appEnabled
    }
  }
`

/*
 * Mutation to save client api key
 */
export const ENABLE_VERIZON_REVEAL_APP = gql`
  mutation EnableVerizonRevealApp($appType: String!, $username: String!, $password: String!) {
    enableVerizonRevealApp(input: { appType: $appType, username: $username, password: $password }) {
      appEnabled
    }
  }
`

export type EnableISAACAppInput = {
  apiKey: string
  appType: AppType
  password: string
  username: string
}

/*
 * Mutation to save client api key
 */
export const ENABLE_ISAAC_APP = gql`
  mutation EnableISAACApp($appType: String!, $apiKey: String!, $username: String!, $password: String!) {
    enableISAACApp(input: { appType: $appType, apiKey: $apiKey, username: $username, password: $password }) {
      appEnabled
    }
  }
`

export type TCustomer = {
  customerID: string
  customerIDInChaine: string | null
  customerNameInChaine: string | null
  integrationConfigurationID?: string
}

/**
 * The input for saving and enabling the FourKites app from the workspace.
 */

export type EnableFourKitesAppInput = {
  apiKey: string
  appType: AppType
  customers: TCustomer[]
}

/**
 * The input for saving and enabling the FourKites app from the workspace.
 */
export type TUpdateFourKitesAppInput = {
  apiKey: string
  appType: AppType
  updates: { created: TCustomer[]; deleted: TCustomer[] }
}

/*
 * Mutation to update customer details inside FourKites App
 */
export const UPDATE_FOUR_KITES_APP = gql`
  mutation UpdateCustomersInFourKitesCarrier($apiKey: String!, $appType: String!, $updates: Updates) {
    updateCustomersInFourKitesCarrier(input: { appType: $appType, apiKey: $apiKey, updates: $updates }) {
      id
    }
  }
`

/**
 * Mutation to request a TMS load sync for a specific TMS App.
 * Currently, only McLeod is supported.
 */
export const REQUEST_TMS_LOAD_SYNC = gql`
  mutation RequestTMSLoadSync($appType: String!) {
    requestTMSLoadSync(input: { appType: $appType }) {
      id
    }
  }
`

export const ENABLE_MCLEOD_APP = gql`
  mutation EnableMcLeodApp(
    $companyID: String!
    $mcLeodInstanceURL: String!
    $mcLeodServerTimezone: String!
    $mcLeodVersion: Float!
    $username: String!
    $password: String!
  ) {
    enableMcLeodApp(
      input: {
        companyID: $companyID
        mcLeodVersion: $mcLeodVersion
        mcLeodInstanceURL: $mcLeodInstanceURL
        mcLeodServerTimezone: $mcLeodServerTimezone
        username: $username
        password: $password
      }
    ) {
      externalWorkspaceID
    }
  }
`

export const UPDATE_MCLEOD_APP = gql`
  mutation UpdateMcLeodApp(
    $companyID: String!
    $mcLeodInstanceURL: String!
    $mcLeodServerTimezone: String!
    $mcLeodVersion: Float!
    $username: String!
    $password: String!
  ) {
    updateMcLeodApp(
      input: {
        companyID: $companyID
        mcLeodVersion: $mcLeodVersion
        mcLeodInstanceURL: $mcLeodInstanceURL
        mcLeodServerTimezone: $mcLeodServerTimezone
        username: $username
        password: $password
      }
    ) {
      message
    }
  }
`

export const ENABLE_HIGHWAY_APP = gql`
  mutation EnableHighwayApp($accessToken: String!) {
    enableHighwayApp(input: { accessToken: $accessToken }) {
      id
    }
  }
`

/**
 * The input for saving and enabling an app from the workspace.
 */
export type EnableMondoTrackingAppInput = {
  apiKey: string
  appType: AppType
}

export const ENABLE_MONDO_TRACKING_APP = gql`
  mutation EnableMondoTrackingApp($apiKey: String!, $appType: String!) {
    enableMondoTrackingApp(input: { apiKey: $apiKey, appType: $appType }) {
      appEnabled
    }
  }
`

/**
 * The input for saving and enabling the US1Network TMS app from the workspace.
 */
export type EnableUS1NetworkTMSAppInput = {
  password: string
  tmsURL: string
  username: string
}

/*
 * Mutation to save US1Network TMS credentials
 */
export const ENABLE_US1_NETWORK_TMS_APP = gql`
  mutation EnableUS1NetworkTMSApp($tmsURL: String!, $username: String!, $password: String!) {
    enableUS1NetworkTMSApp(input: { tmsURL: $tmsURL, username: $username, password: $password }) {
      appEnabled
      password
      tmsURL
      username
    }
  }
`

/**
 * The input for saving and enabling an app from the workspace.
 */
export type EnableOneStepGPSAppInput = {
  apiKey: string
  appType: AppType
}

/*
 * Mutation to save client api key
 */
export const ENABLE_ONE_STEP_GPS_APP = gql`
  mutation EnableOneStepGPSApp($appType: String!, $apiKey: String!) {
    enableOneStepGPSApp(input: { appType: $appType, apiKey: $apiKey }) {
      appEnabled
    }
  }
`
